import * as React from 'react';
import { Link } from 'react-router-dom';

import { renderNavList } from '../../Shared';
import { MOUSEDOWN, KEYDOWN, } from '../../Shared/utils/eventNames'
import { ESCAPE, } from '../../Shared/utils/keyNames'

const ACTIVITY_SCORES = 'Activity Scores Report';
const SKILLS_REPORT = 'Skills Report'
const INTEGRATIONS = 'Integrations';
const OVERVIEW = 'Overview';
const SUBSCRIPTIONS = 'Subscriptions';
const USAGE_SNAPSHOT_REPORT = 'Usage Snapshot Report'
const DATA_EXPORT = 'Student Activity Data Export'
const TEACHER_USAGE_EXPORT = 'Teacher Usage Export'
const DIAGNOSTIC_GROWTH_REPORT = 'Diagnostic Growth Report'
const ADMIN_SKILLS_REPORT = 'Skills Report'
const ADMIN_STANDARDS_REPORT = 'Standards Report'
const PREMIUM_REPORTS = 'Premium Reports'
const ACCOUNT_MANAGEMENT = 'Account Management'

const baseTabs = {
  [OVERVIEW]: {
    label: OVERVIEW,
    url: '/teachers/premium_hub'
  },
  [SUBSCRIPTIONS]: {
    label: SUBSCRIPTIONS,
    url: '/teachers/premium_hub/school_subscriptions'
  },
  [ACCOUNT_MANAGEMENT]: {
    label: ACCOUNT_MANAGEMENT,
    url: '/teachers/premium_hub/account_management'
  },
  [INTEGRATIONS]: {
    label: INTEGRATIONS,
    url: '/teachers/premium_hub/integrations'
  },
}

const mobileTabs = {
  ...baseTabs,
  [ACTIVITY_SCORES]: {
    label: ACTIVITY_SCORES,
    url: '/teachers/premium_hub/district_activity_scores'
  },
  [USAGE_SNAPSHOT_REPORT]: {
    label: USAGE_SNAPSHOT_REPORT,
    url: '/teachers/premium_hub/usage_snapshot_report'
  },
  [DIAGNOSTIC_GROWTH_REPORT]: {
    label: DIAGNOSTIC_GROWTH_REPORT,
    url: '/teachers/premium_hub/diagnostic_growth_report',
  },
  [ADMIN_SKILLS_REPORT]: {
    label: ADMIN_SKILLS_REPORT,
    url: '/teachers/premium_hub/admin_skills_report',
  },
  [ADMIN_STANDARDS_REPORT]: {
    label: ADMIN_STANDARDS_REPORT,
    url: '/teachers/premium_hub/admin_standards_report',
  },
  [TEACHER_USAGE_EXPORT]: {
    label: TEACHER_USAGE_EXPORT,
    url: '/teachers/premium_hub/teacher_usage_export'
  },
  [DATA_EXPORT]: {
    label: DATA_EXPORT,
    url: '/teachers/premium_hub/data_export'
  }
}

const premiumReportDropdownItems = [
  {
    label: USAGE_SNAPSHOT_REPORT,
    url: '/teachers/premium_hub/usage_snapshot_report',
  },
  {
    label: DIAGNOSTIC_GROWTH_REPORT,
    url: '/teachers/premium_hub/diagnostic_growth_report',
  },
  {
    label: ADMIN_SKILLS_REPORT,
    url: '/teachers/premium_hub/admin_skills_report',
    new: true
  },
  {
    label: ADMIN_STANDARDS_REPORT,
    url: '/teachers/premium_hub/admin_standards_report',
    new: true
  },
  {
    label: TEACHER_USAGE_EXPORT,
    url: '/teachers/premium_hub/teacher_usage_export',
    new: true
  },
  {
    label: DATA_EXPORT,
    url: '/teachers/premium_hub/data_export',
  },
  {
    label: ACTIVITY_SCORES,
    url: '/teachers/premium_hub/district_activity_scores'
  },
]

const PremiumReportsDropdown = ({ activeTab }) => {
  const dropdownId = "premium-reports-nav-dropdown"
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef(null)
  const dropdownRef = React.useRef(null);
  const buttonRef = React.useRef(null);

  React.useEffect(() => {
    document.addEventListener(MOUSEDOWN, handleClickOutside);
    document.addEventListener(KEYDOWN, handleKeyDown);
    return () => {
      document.removeEventListener(MOUSEDOWN, handleClickOutside);
      document.removeEventListener(KEYDOWN, handleKeyDown);
    };
  }, [dropdownRef, isOpen]);

  const activeClass = activeTab === PREMIUM_REPORTS ? 'active' : '';
  const openClass = isOpen ? 'open' : '';

  function toggleDropdown() {
    if (isOpen) {
      closeDropdown()
    } else {
      setIsOpen(true);

      // setTimeout here is a workaround to make sure that the dropdownRef is actually available in the DOM by the time this executes (it pushes execution to the end of the event queue, after the rerender)
      setTimeout(() => {
        const firstItem = dropdownRef.current.querySelector('a');
        if (firstItem) firstItem.focus();
      }, 0);
    }
  }

  function closeDropdown() {
    if (!isOpen) { return }

    setIsOpen(false);
    // Return focus to the button when dropdown closes
    buttonRef.current.focus();
  }

  function handleClickOutside(event) {
    if (containerRef.current && !containerRef.current.contains(event.target)) {
      closeDropdown();
    }
  }

  function handleKeyDown(event) {
    if (event.key === ESCAPE) {
      closeDropdown();
    }
  }

  function renderDropdown() {
    return (
      <div className={dropdownId} id={dropdownId} ref={dropdownRef} role="menu">
        {premiumReportDropdownItems.map((item) => (
          <Link className="focus-on-dark" key={item.label} onClick={closeDropdown} role="menuitem" to={item.url}>
            {item.label}
            {item.new ? <span className="new-tag">NEW</span> : null}
          </Link>
        ))}
      </div>
    );
  }

  return (
    <li className="premium-reports-tab" ref={containerRef}>
      <button
        aria-controls={dropdownId}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={`premium-reports-button focus-on-dark ${activeClass} ${openClass}`}
        onClick={toggleDropdown}
        ref={buttonRef}
        type="button"
      >
        <span>Premium Reports</span>
      </button>
      {isOpen ? renderDropdown() : null}
    </li>
  );
};

const AdminSubnav = ({ path }) => {

  const [activeTab, setActiveTab] = React.useState<string>('');
  const [dropdownOpen, setDropdownOpen] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (path.pathname) {
      determineActiveTab()
    }
  }, [path.pathname])

  function determineActiveTab() {
    const { pathname } = path;

    const reportPaths = ['/district_activity_scores', '/usage_snapshot_report', '/data_export', '/teacher_usage_export', '/diagnostic_growth_report', '/admin_skills_report', '/admin_standards_report']

    if (reportPaths.find(path => pathname.includes(path))) {
      setActiveTab(PREMIUM_REPORTS)
    } else if (pathname.includes('/school_subscriptions')) {
      setActiveTab(SUBSCRIPTIONS)
    } else if (pathname.includes('/account_management')) {
      setActiveTab(ACCOUNT_MANAGEMENT)
    } else if (pathname.includes('/integrations')) {
      setActiveTab(INTEGRATIONS)
    } else if (pathname.includes('/premium_hub')) {
      setActiveTab(OVERVIEW)
    }
  }

  function handleDropdownClick() {
    setDropdownOpen(!dropdownOpen);
  }

  function handleLinkClick() {
    setDropdownOpen(false);
  }

  const dropdownClass = dropdownOpen ? 'open' : '';

  return(
    <React.Fragment>
      <div className="tab-subnavigation-wrapper mobile class-subnav premium-hub-subnav red">
        <div className="dropdown-container">
          <div className={dropdownClass}>
            <button aria-label="Navigation menu" className="interactive-wrapper focus-on-dark" id="mobile-subnav-dropdown" onClick={handleDropdownClick} type='button'>
              <p>{activeTab}</p>
              <i className="fa fa-thin fa-angle-down" />
            </button>
            {renderNavList({ ariaLabel: 'Premium Hub', tabs: mobileTabs, handleLinkClick: handleLinkClick, activeTab, listClass: 'dropdown-menu' })}
          </div>
        </div>
      </div >
      <div className="tab-subnavigation-wrapper desktop class-subnav premium-hub-subnav">
        <div className="container">
          {renderNavList({ ariaLabel: 'Premium Hub', tabs: baseTabs, handleLinkClick: handleLinkClick, activeTab, childElement: <PremiumReportsDropdown activeTab={activeTab} /> })}
        </div>
      </div>
    </React.Fragment>
  );
};

export default AdminSubnav
